'use client';

import { DATA_ROOT_PATH, METRICS_PATH, STATISTICS_PATH } from '@/constants';
import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

export type DetailedNavElement = {
  type: 'metric' | 'statistic';
  id: number;
};

type DetailedNavContext = {
  navigationData: DetailedNavElement[];
  setNavigationData: (
    data: DetailedNavElement[],
    id: number,
    noNavigate?: boolean,
  ) => void;
  next: () => void;
  previous: () => void;
  activeElement: DetailedNavElement;
  previousAction: 'previous' | 'next' | null;
  canNavigateForward: boolean;
  canNavigateBackward: boolean;
};

const DetailedNavigationContext = createContext<DetailedNavContext>(
  null as unknown as DetailedNavContext,
);

export const useDetailedNavigation = () => {
  const context = useContext(DetailedNavigationContext);

  if (!context) {
    throw new Error(
      'useDetailedNavigation must be used within a DetailedNavigationProvider',
    );
  }

  return context;
};

export const DetailedNavigationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const path = usePathname();

  const [lastClicked, setLastClicked] = useState<'previous' | 'next' | null>(
    null,
  );
  const router = useRouter();

  const [canNavigateForward, setCanNavigateForward] = useState(false);
  const [canNavigateBackward, setCanNavigateBackward] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [navigationData, setNavigationData] = useState<DetailedNavElement[]>(
    [],
  );

  const clear = () => {
    setCanNavigateForward(false);
    setCanNavigateBackward(false);
    setCurrentIndex(0);
    setLastClicked(null);
    setNavigationData([]);
  };

  useEffect(() => {
    // Clear navigation data if the path is not a detailed view path to avoid data carousels in the wrong context like linking detailed view from header
    if (!path.startsWith(DATA_ROOT_PATH)) {
      clear();
    }
  }, [path]);

  const getPath = (element: DetailedNavElement) => {
    let path = '';
    if (element.type === 'metric') {
      path = `${METRICS_PATH}/${element.id}`;
    } else {
      path = `${STATISTICS_PATH}/${element.id}`;
    }
    return path;
  };

  const setNavData = (
    data: DetailedNavElement[],
    id: number,
    noNavigate = false,
  ) => {
    if (!data.length) {
      clear();
      return;
    }

    const currentIndex = data.findIndex((element) => element.id === id);
    const element = data[currentIndex];

    setCanNavigateBackward(currentIndex - 1 >= 0);
    setCanNavigateForward(currentIndex + 1 <= data.length - 1);
    setCurrentIndex(currentIndex);
    setNavigationData(data);
    setLastClicked(null);

    if (!noNavigate) {
      router.push(getPath(element));
    }
  };

  const next = () => {
    if (currentIndex < navigationData.length - 1) {
      setLastClicked('next');
      const newIndex = currentIndex + 1;
      const element = navigationData[newIndex];

      setCanNavigateBackward(newIndex - 1 >= 0);
      setCanNavigateForward(newIndex + 1 <= navigationData.length - 1);
      setCurrentIndex(newIndex);
      router.replace(getPath(element));
    }
  };

  const previous = () => {
    if (currentIndex > 0) {
      setLastClicked('previous');
      const newIndex = currentIndex - 1;
      const element = navigationData[newIndex];

      setCanNavigateBackward(newIndex - 1 >= 0);
      setCanNavigateForward(newIndex + 1 <= navigationData.length - 1);
      setCurrentIndex(newIndex);
      router.replace(getPath(element));
    }
  };

  return (
    <DetailedNavigationContext
      value={{
        navigationData,
        setNavigationData: setNavData,
        next,
        previous,
        activeElement: navigationData[currentIndex],
        previousAction: lastClicked,
        canNavigateForward,
        canNavigateBackward,
      }}
    >
      {children}
    </DetailedNavigationContext>
  );
};
